<template>
  <node-view-wrapper
    :data-block-id="props?.node?.attrs?.['block-id']"
    :class="props.extension.options.className"
    class="my-2"
  >
    <div
      class="callout"
      :class="props.node.attrs.type"
    >
      <div class="flex w-full">
        <v-popover
          interactive
          tag="div"
          content-tag="div"
          placement="bottom-start"
          trigger="click"
          class="h-6"
          :role="'poper'"
          :show-on-create="props.node.attrs.showDropdownOnCreate"
          :append-to="body"
          :on-show="handleOnShow"
          :on-hide="handleOnHide"
        >
          <template #default="{ state }">
            <div
              class="callout-icon-wrap"
              :class="{
                'bg-attention-icon': state.isVisible && props.editor.isEditable,
              }"
            >
              <em
                :style="getCustomIconStyle(iconPath(calloutIcons[props.node.attrs.type] || 'info'))"
                class="callout-icon custom-icons"
                contenteditable="false"
              />
            </div>
          </template>
          <template #content="{ state, hide }">
            <FilterDropdown
              v-if="state.isVisible"
              ref="filterPopup"
              :search-results="dropdownItems"
              :selected-item-check="
                dropdownItems.findIndex((item) => item.id === props.node.attrs.type)
              "
              :on-item-right-side-click="(item) => handleClick(item, hide)"
              :on-item-click="(item) => handleClick(item, hide)"
              :hide-filter-popup="() => {}"
              :placeholder="''"
              :show-search-input="false"
              @esc="handleEsc(hide)"
            />
          </template>
        </v-popover>
        <span
          class="callout-content"
          :data-placeholder="translate('typeSomething')"
          :contenteditable="props.editor.isEditable"
          :class="{
            'suggestion-active': mentionActive,
          }"
        >
          <node-view-content />
        </span>
      </div>
    </div>
  </node-view-wrapper>
</template>

<script setup lang="ts">
import { NodeViewWrapper, NodeViewContent } from '@tiptap/vue-3';
import { ref, onMounted, nextTick, computed } from 'vue';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Editor } from '@tiptap/core';
import { CalloutTypes } from '@superthread-com/extension-callouts/src/index';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
const { iconPath, getCustomIconStyle } = useCustomIcon();
import useTranslate from '@/utilities/composables/useTranslate';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown.vue';

/**
 * The correct type definition would be:
 * const props = defineProps<typeof nodeViewProps>();
 *
 * However, that is not supported in Vue 2, so the type must be
 * specified like this.
 */
const props = defineProps<{
  node: ProseMirrorNode;
  deleteNode: (() => () => void) | undefined;
  editor: Editor;
  updateAttributes: (attrs: Record<string, any>) => void;
  extension: any;
}>();
const { translate } = useTranslate();
const calloutIcons = ref({
  info: 'info',
  success: 'check-square',
  warning: 'alert-triangle',
  error: 'x-octagon',
} as Record<string, string>);

const calloutTranslationKeys = {
  info: 'information',
  success: 'success',
  warning: 'warning',
  error: 'error',
} as Record<string, string>;

const showDropdown = ref(false);
const body = ref(window.document.body);
const filterPopup = ref();

const mentionActive = computed(() => props.editor.extensionStorage.mention.mentionDropdownActive);

onMounted(() => {
  nextTick(() => {
    if (props.editor.isEditable) {
      showDropdown.value = true;
    }
  });
});

const dropdownItems = (Object.keys(CalloutTypes) as Array<keyof typeof CalloutTypes>).map(
  (key: keyof typeof CalloutTypes) => {
    const id = CalloutTypes[key];
    return {
      id: id,
      customIcon: true,
      lighter: true,
      icon: calloutIcons.value[id],
      label: translate(calloutTranslationKeys[id]),
    };
  }
);

const handleOnShow = () => {
  if (!props.editor.isEditable) return false;
  nextTick(() => {
    filterPopup?.value?.searchInput?.focus();
  });
  return true;
};

const handleClick = (item: any, hide: Function) => {
  hide?.();
  props.updateAttributes({ type: item.id });
  props.editor.commands.focus();
};

const handleEsc = (hide: Function) => {
  hide?.();
  props.editor.commands.focus();
};

const handleOnHide = () => {
  props.updateAttributes({ showDropdownOnCreate: false });
};
</script>

<style lang="scss">
.page-comment-editor,
.description-row,
.commented-text-context {
  .st-editor-content,
  .description-text {
    .callout {
      font-size: 14px !important;
      line-height: 20px !important;
      .callout-icon-wrap {
        height: 20px !important;
        width: 20px !important;
        em {
          height: 14px !important;
          width: 14px !important;
        }
      }
    }
  }
}
.st-editor {
  &.display {
    .callout {
      .callout-icon-wrap {
        @apply hover:bg-transparent cursor-default;
      }
      &.is-empty {
        .callout-content {
          &:before {
            // do not display the placeholder in editor display mode
            display: none;
          }
        }
      }
    }
  }
}
.callout {
  @apply relative flex border border-solid p-4 box-border rounded;
  .filter-popup {
    width: 240px;
  }
  .callout-icon-wrap {
    @apply w-6 h-6 flex items-center justify-center rounded hover:bg-callout-iconHover cursor-pointer;
    .callout-icon {
      @apply h-4 w-4 m-0 bg-icon-default;
    }
  }
  &.is-empty {
    .callout-content {
      &:before {
        content: attr(data-placeholder);
        @apply text-text-placeholder absolute inline-block cursor-text;
      }
      &.suggestion-active {
        &:before {
          // do not display the placeholder in editor display mode
          display: none;
        }
      }
    }
  }

  .callout-content {
    @apply text-text-default ml-2 min-w-1;
    width: calc(100% - 32px); // 24px for the icon and 8px for the margin
  }

  &.info {
    @apply bg-callout-info border-callout-infoBorder;
  }

  &.warning {
    @apply bg-callout-warning border-callout-warningBorder;
  }

  &.error {
    @apply bg-callout-error border-callout-errorBorder;
  }

  &.success {
    @apply bg-callout-success border-callout-successBorder;
  }
}
</style>
